import React, { useState, useRef } from "react";
import {
  Autocomplete,
  TextField,
  Box,
  Typography,
  IconButton,
  Tooltip,
} from "@mui/material";
import { useGridApiContext } from "@mui/x-data-grid";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AllocationProfileViewDialog from "./AllocationProfileViewDialog";

function AllocationProfileAutocompleteEditCell({
  id,
  value,
  field,
  options,
  optionIdField,
  optionLabelField,
  multiple = false,
  label,
}) {
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [profileToView, setProfileToView] = useState(null);
  const apiRef = useGridApiContext();
  const ref = useRef();

  const handleChange = (event, newValue) => {
    const valueToStore = multiple
      ? newValue?.map((option) => option[optionIdField]) || []
      : newValue?.[optionIdField] || null;

    apiRef.current.setEditCellValue({ id, field, value: valueToStore });
  };

  const handleViewClick = (event, profile) => {
    event.preventDefault();
    event.stopPropagation();
    setProfileToView(profile);
    setViewDialogOpen(true);
  };

  const selectedValue = multiple
    ? options.filter((option) => value?.includes(option[optionIdField])) || []
    : options.find((option) => option[optionIdField] === value) || null;

  const renderOption = (props, option) => {
    const { key, ...otherProps } = props;

    return (
      <Box
        key={key}
        component="li"
        {...otherProps}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          "&:hover .view-button": {
            opacity: 1,
          },
        }}
      >
        <Box
          sx={{ display: "flex", alignItems: "center", minWidth: 0, flex: 1 }}
        >
          <Tooltip title="View allocation profile details">
            <IconButton
              className="view-button"
              size="small"
              onClick={(event) => handleViewClick(event, option)}
              sx={{
                mr: 1,
                opacity: 0.6,
                transition: "opacity 0.2s",
                "&:hover": {
                  opacity: 1,
                  backgroundColor: "action.hover",
                },
              }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Typography
            sx={{
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {option[optionLabelField]}
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
      }}
    >
      <Autocomplete
        ref={ref}
        value={selectedValue}
        onChange={handleChange}
        options={options}
        getOptionLabel={(option) => option[optionLabelField] || ""}
        isOptionEqualToValue={(option, value) =>
          option[optionIdField] === value?.[optionIdField]
        }
        multiple={multiple}
        size="small"
        fullWidth
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            variant="outlined"
            size="small"
            fullWidth
            sx={{
              display: "flex",
              alignItems: "center",
              width: "100%",
              "& .MuiOutlinedInput-root": {
                height: "100%",
                minHeight: "auto",
                "& fieldset": {
                  border: "none",
                },
                "&:hover fieldset": {
                  border: "1px solid rgba(0, 0, 0, 0.23)",
                },
                "&.Mui-focused fieldset": {
                  border: "2px solid #1976d2",
                },
              },
              "& .MuiInputLabel-root": {
                transform: "translate(14px, 8px) scale(1)",
                "&.Mui-focused, &.MuiFormLabel-filled": {
                  transform: "translate(14px, -9px) scale(0.75)",
                },
              },
            }}
          />
        )}
        renderOption={renderOption}
        componentsProps={{
          popper: {
            sx: {
              "& .MuiAutocomplete-option": {
                paddingLeft: 1,
                paddingRight: 1,
              },
            },
          },
        }}
      />

      <AllocationProfileViewDialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        profile={profileToView}
      />
    </Box>
  );
}

export default AllocationProfileAutocompleteEditCell;
